// components/StyledTimePicker.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ClockIcon from "../../assets/icons/ClockIcon";
import { useTheme } from "../../middleware/ThemeContext";
import { Theme } from "../../utils/themes";

const TimePickerWrap = styled.div`
  position: relative;
  width: 100%;
`;

const TimeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 220px;
  height: 42px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 500;
  font-family: "Raleway",sans-serif;
  color: ${({theme}) => theme.CorporateTheme.colors.headerColor};
  background-color: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  }

  svg {
    font-size: 16px;
  }
`;

const TimePanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  display: flex;
  gap: 0;
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  border-radius: 6px;
  background: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 72px;
  max-height: 180px;
  overflow-y: auto;
  padding: 2px;
`;

const TimeOption = styled.button<{ $selected?: boolean , theme:Theme }>`
  border: none;
  background: ${({ $selected, theme }) =>
    $selected ? theme.CorporateTheme.colors.primary : "transparent"};
  color: ${({ $selected, theme }) =>
    $selected ? theme.CorporateTheme.colors.background : theme.CorporateTheme.colors.headerColor};
  font-size: 14px;
  font-weight: 600;
  padding: 10px 8px;
  border-radius: 3px;
  cursor: pointer;
  text-align: center;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.CorporateTheme.colors.primary : theme.CorporateTheme.colors.filterBG};
  }
`;

type Props = {
  value: string; // format: "HH:mm"
  onChange: (value: string) => void;
};

const StyledTimePicker: React.FC<Props> = ({ value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {theme} = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current || wrapperRef.current.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const [selectedHour, selectedMinute, selectedPeriod] = useMemo(() => {
    if (!value) return ["12", "00", "AM"];
    const [rawHour, minute] = value.split(":");
    const hourNumber = Number(rawHour);
    const period = hourNumber >= 12 ? "PM" : "AM";
    const displayHour = hourNumber % 12 || 12;
    return [String(displayHour).padStart(2, "0"), minute ?? "00", period];
  }, [value]);

  const hourOptions = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
  const minuteOptions = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));
  const periodOptions = ["AM", "PM"];

  const handleUpdate = (nextHour?: string, nextMinute?: string, nextPeriod?: string) => {
    const hourLabel = nextHour ?? selectedHour;
    const minuteLabel = nextMinute ?? selectedMinute;
    const periodLabel = nextPeriod ?? selectedPeriod;
    const hourNumber = Number(hourLabel) % 12;
    const normalizedHour = periodLabel === "PM" ? hourNumber + 12 : hourNumber;
    const formattedHour = String(normalizedHour % 24).padStart(2, "0");
    onChange(`${formattedHour}:${minuteLabel}`);
  };

  const formatAMPM = (time: string) => {
    if (!time) return "Select time";
    const [h, m] = time.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh}:${m.toString().padStart(2, "0")} ${ampm}`;
  };

  return (
    <TimePickerWrap ref={wrapperRef}>
      <TimeButton theme={theme} onClick={() => setIsOpen((prev) => !prev)} type="button">
        <span>{formatAMPM(value)}</span>
        <ClockIcon color={theme.CorporateTheme.colors.headerColor} />
      </TimeButton>
      {isOpen && (
        <TimePanel theme={theme}>
          <TimeColumn>
            {hourOptions.map((hour) => (
              <TimeOption  key={hour} type="button" theme={theme} $selected={hour === selectedHour} onClick={() => handleUpdate(hour, undefined, undefined)}>
                {hour}
              </TimeOption>
            ))}
          </TimeColumn>
          <TimeColumn>
            {minuteOptions.map((minute) => (
              <TimeOption key={minute} type="button" theme={theme} $selected={minute === selectedMinute} onClick={() => handleUpdate(undefined, minute, undefined)}>
                {minute}
              </TimeOption>
            ))}
          </TimeColumn>
          <TimeColumn style={{ width: "58px" }}>
            {periodOptions.map((period) => (
              <TimeOption key={period} type="button" theme={theme} $selected={period === selectedPeriod} onClick={() => handleUpdate(undefined, undefined, period)}>
                {period}
              </TimeOption>
            ))}
          </TimeColumn>
        </TimePanel>
      )}
    </TimePickerWrap>
  );
};

export default StyledTimePicker;
